import { db } from "./firebaseSetup";
import { doc, getDoc, Timestamp } from "firebase/firestore";
import { PageBuilderPageModel } from "@de-trustleap/shared-models";
import { renderPage } from "./render/renderPage";
import { Spinner } from "./helpers/spinner";
import { Logger } from "./utils/logger";
import { setLandingPageData, setLandingPageContent, setLandingPageId } from "./context/landingPageContext";
import { setRecommendationData } from "./context/recommendationContext";
import { loadStaticDefaultLandingPage } from "./utils/staticDefaultPage";
import { isPreviewMode } from "./utils/previewUtils";

// Empfehlung-ID aus URL lesen
export function getRecommendationIdFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("id");
}

function isExpired(timestamp: Timestamp): boolean {
    return timestamp.toDate() < new Date();
}

async function getRecommendationDataById(id: string): Promise<any | null> {
    try {
        const recSnap = await getDoc(doc(db, "recommendations", id));
        return recSnap.exists() ? recSnap.data() : null;
    } catch (err) {
        Logger.error("❌ Fehler beim Abruf der Recommendation", err);
        return null;
    }
}

async function getLandingPageDataById(id: string): Promise<any | null> {
    try {
        const snap = await getDoc(doc(db, "landingPages", id));
        return snap.exists() ? snap.data() : null;
    } catch (err) {
        Logger.error("❌ Fehler beim Abruf der LandingPage", err);
        return null;
    }
}

async function setTitleFromRecommendationOrLandingPage(data: any, isRecommendation = true) {
    const provider = isRecommendation ? data?.serviceProviderName ?? "" : data?.companyData?.name ?? "";
    if (!provider) return;

    const currentPath = window.location.pathname;
    const prefix =
        currentPath.includes("datenschutz") ? "Datenschutzerklärung von" :
        currentPath.includes("impressum") ? "Impressum von" :
        currentPath.includes("erstinformation") ? "Erstinformation von" :
        currentPath.includes("agb") ? "AGB von" : "";

    if (prefix) {
        document.title = `${prefix} ${provider}`;
        Logger.info("✅ Titel gesetzt:", document.title);
    }
}

async function loadAndRenderLandingPage(landingPageData: any, landingPageId: string): Promise<boolean> {
    if (!landingPageData) {
        Logger.warn(`⚠️ LandingPage ${landingPageId} nicht gefunden.`);
        return false;
    }

    setLandingPageData(landingPageData);
    setLandingPageId(landingPageId);

    const contentID = landingPageData?.contentID;
    if (!contentID) {
        Logger.warn(`⚠️ Kein contentID in LandingPage ${landingPageId}.`);
        return false;
    }

    const contentSnap = await getDoc(doc(db, "landingPagesContent", contentID));
    if (!contentSnap.exists()) {
        Logger.warn(`⚠️ Content ${contentID} nicht gefunden.`);
        return false;
    }

    const pageContent = contentSnap.data();
    Logger.info("✅ LandingPage Content geladen:", pageContent);
    setLandingPageContent(pageContent);

    const pageModel = new PageBuilderPageModel(pageContent);
    renderPage(pageModel);
    return true;
}

function injectFallbackStyles() {
    document.querySelector('link[href*="main.css"]')?.remove();

    const fallbackCss = document.createElement("link");
    fallbackCss.rel = "stylesheet";
    fallbackCss.href = "/styles/fallback.css";
    document.head.appendChild(fallbackCss);
}

function showFallbackPage(title: string, message: string) {
    injectFallbackStyles();
    document.body.innerHTML = `
        <main class="fallback-container">
            <h1>${title}</h1>
            <p>${message}</p>
            <hr>
            <p style="color: #999; font-size: 14px;">
                Falls du denkst, dass dies ein Fehler ist, kontaktiere bitte den Anbieter oder versuche es später erneut.
            </p>
        </main>
    `;
}

// 🟢 Hauptfunktion
export async function getDocument(): Promise<boolean> {
    const spinner = Spinner.getInstance("loading-spinner");
    spinner.show();

    const id = getRecommendationIdFromUrl();
    Logger.info("🔍 Recommendation-ID aus URL:", id);

    try {
        if (!id) {
            Logger.warn("❌ Keine ID in der URL vorhanden.");
            showFallbackPage("Ungültiger Aufruf", "Keine ID angegeben. Bitte nutze einen gültigen Empfehlungslink.");
            return false;
        }

        const recommendation = await getRecommendationDataById(id);
        if (!recommendation) {
            Logger.warn(`⚠️ Recommendation ${id} nicht gefunden.`);
            showFallbackPage("Empfehlung ungültig", "Dieser Empfehlungslink ist nicht mehr gültig.");
            return false;
        }

        setRecommendationData(recommendation);
        await setTitleFromRecommendationOrLandingPage(recommendation, true);

        if (recommendation.expiresAt && isExpired(recommendation.expiresAt)) {
            const promoterName = recommendation.promoterName ?? "dem Empfehlungsgeber";
            showFallbackPage(
                "Empfehlung abgelaufen",
                `Diese Empfehlung ist nicht mehr gültig. Bitte kontaktiere ${promoterName} für eine neue Version.`
            );
            return true;
        }

        const landingPageId = recommendation?.landingPageID;
        const landingPageData = landingPageId ? await getLandingPageDataById(landingPageId) : null;
        const isDisabled = landingPageData?.isActive !== true;
        const isDefault = landingPageData?.isDefaultPage === true;

        // ✅ Preview-Modus → Immer rendern, egal ob aktiv oder nicht
        if (isPreviewMode()) {
            if (landingPageData) {
                Logger.info(`🔎 Preview-Modus → Deaktivierung ignoriert. LandingPage ${landingPageId} wird gerendert.`);
                return await loadAndRenderLandingPage(landingPageData, landingPageId!);
            } else if (recommendation.defaultLandingPageID) {
                const fallbackData = await getLandingPageDataById(recommendation.defaultLandingPageID);
                if (fallbackData) {
                    const metaData = {
                        companyData: fallbackData.companyData,
                        contactEmailAddress: fallbackData.contactEmailAddress,
                        landingPageId: recommendation.defaultLandingPageID,
                        thumbnailDownloadURL: fallbackData.thumbnailDownloadURL,
                        landingPageImageDownloadURL: fallbackData.landingPageImageDownloadURL
                    };
                    await loadStaticDefaultLandingPage(metaData);
                    return true;
                }
            }
        }

        // 👉 Normale aktive LandingPage
        if (landingPageData && (!isDisabled || isDefault)) {
            return await loadAndRenderLandingPage(landingPageData, landingPageId!);
        }

        // 👉 Wenn deaktiviert oder nicht gefunden → Default LandingPage laden
        if ((isDisabled || !landingPageData) && recommendation.defaultLandingPageID) {
            const fallbackData = await getLandingPageDataById(recommendation.defaultLandingPageID);
            if (fallbackData && fallbackData.isDefaultPage === true) {
                const metaData = {
                    companyData: fallbackData.companyData,
                    contactEmailAddress: fallbackData.contactEmailAddress,
                    landingPageId: recommendation.defaultLandingPageID,
                    thumbnailDownloadURL: fallbackData.thumbnailDownloadURL,
                    landingPageImageDownloadURL: fallbackData.landingPageImageDownloadURL
                };
                await loadStaticDefaultLandingPage(metaData);
                return true;
            } else {
                showFallbackPage("Fehler", "Die Standard-Landingpage konnte nicht geladen werden.");
                return false;
            }
        }

        // Wenn keine Option greift → Fehler
        showFallbackPage("Seite deaktiviert", "Die LandingPage ist momentan nicht erreichbar.");
        return false;

    } catch (error) {
        Logger.error("❌ Schwerer Fehler beim Laden der Seite", error);
        showFallbackPage("Fehler beim Laden", "Beim Laden der Seite ist ein Fehler aufgetreten. Bitte versuche es später erneut.");
        return false;
    } finally {
        spinner.hide();
    }
}
